import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Fab,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Chip
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { useQuery } from 'react-query'
import { ConfigValue } from '../../Config'
import useAuth from '../../Hooks/use-auth'

const getEmailDomain = email => email?.split('@')[1] || ''

// fetch company users
async function fetchUsersByCompany() {
  const res = await fetch(
    `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/creatorUsers/users_by_company`
  )
  const raw = await res.json()
  const filtered = {}
  Object.keys(raw).forEach(company => {
    const users = raw[company].filter(
      u => u.email && !u.email.endsWith('@gmail.com')
    )
    if (users.length) filtered[company] = users
  })
  return filtered
}

// fetch CRM contacts
async function fetchContacts() {
  const res = await fetch(
    `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/crm/contacts`
  )
  const raw = await res.json()
  return raw.filter(c => c.email && !c.email.endsWith('@gmail.com'))
}

export default function AgencyOutbound() {
  // get logged-in user id
  const { getCurrrentUser } = useAuth()
  const userId = getCurrrentUser()?.id

  // selection + dialog state
  const [selected, setSelected] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [rate, setRate] = useState('')
  const [ccEmail, setCcEmail] = useState('')
  const [sending, setSending] = useState(false)

  // CredX credits & sent-count
  const [credits, setCredits] = useState(0)
  const [pitchesSent, setPitchesSent] = useState(0)

  // load credits on mount / when userId changes
  useEffect(() => {
    if (!userId) return
    fetch(`${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/credits?user_id=${userId}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch credits')
        return res.json()
      })
      .then(data => setCredits(data.credits))
      .catch(err => console.error(err))
  }, [userId])

  // queries
  const { data: usersByCompany, isLoading: loadingUsers } = useQuery(
    'usersByCompany',
    fetchUsersByCompany
  )
  const { data: contacts, isLoading: loadingContacts } = useQuery(
    'contacts',
    fetchContacts
  )

  const processedUsers = usersByCompany || {}
  const processedContacts = contacts || []
  const allLoaded = !loadingUsers && !loadingContacts

  // build top-10 domain recommendations
  const recommendations = useMemo(() => {
    const domainCounts = {}
    processedContacts.forEach(c => {
      const dom = getEmailDomain(c.email)
      if (!dom) return
      if (!domainCounts[dom]) domainCounts[dom] = { count: 0, contacts: [] }
      domainCounts[dom].count++
      domainCounts[dom].contacts.push(c)
    })
    const sortedDomains = Object.keys(domainCounts).sort(
      (a, b) => domainCounts[b].count - domainCounts[a].count
    )
    return sortedDomains
      .flatMap(d => domainCounts[d].contacts)
      .slice(0, 10)
  }, [processedContacts])

  // select / deselect
  const toggle = id =>
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )

  // open dialog only if we have credits & a selection
  const handleOpen = () => {
    if (selected.length === 0) {
      return alert('Select at least one contact or company first.')
    }
    if (selected.length > credits) {
      return alert('Not enough credits.')
    }
    setDialogOpen(true)
  }

  // send pitches
  const sendPitch = async () => {
    if (!ccEmail) {
      return alert('Your own email (CC) is required.')
    }
    setSending(true)
    try {
      // 1. Subtract credits
      const subRes = await fetch(
        `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/subtract_credx`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            email_count: selected.length
          })
        }
      )
      if (!subRes.ok) {
        const { error } = await subRes.json()
        throw new Error(error || 'Failed to subtract credits')
      }
      const { credits: newCredits } = await subRes.json()
      setCredits(newCredits)
      setPitchesSent(ps => ps + selected.length)

      // 2. Send each email
      for (const id of selected) {
        let recipients = []
        // if it's a company key
        if (processedUsers[id]) {
          recipients = processedUsers[id].map(u => u.email)
        } else {
          // else it's an individual contact email
          recipients = [id]
        }
        for (const email of recipients) {
          const mailRes = await fetch(
            `${ConfigValue.PUBLIC_REST_API_ENDPOINT}/credx/send_pitch_email`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: userId,
                recipient_email: email,
                subject,
                body: `${body}\n\nRate: ${rate}`,
                cc_email: ccEmail
              })
            }
          )
          if (!mailRes.ok) {
            const { error } = await mailRes.json()
            console.warn(`Failed to send to ${email}:`, error)
          }
        }
      }
      // close & reset
      setDialogOpen(false)
      setSelected([])
      setSubject('')
      setBody('')
      setRate('')
      setCcEmail('')
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setSending(false)
    }
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom fontWeight={800}>
        Outbound Pitches
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography>Pitches Sent: {pitchesSent}</Typography>
        <Typography>Credits: {credits}</Typography>
      </Box>

      {!allLoaded && <CircularProgress sx={{ mt: 4 }} />}

      {allLoaded && (
        <>
          <Typography variant="h6" sx={{ mt: 4 }}>
            Recommendations
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Company</TableCell>
                  <TableCell>Domain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recommendations.map(c => {
                  const id = c.email
                  return (
                    <TableRow key={id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(id)}
                          onChange={() => toggle(id)}
                        />
                      </TableCell>
                      <TableCell>
                        {c.company_name || getEmailDomain(c.email)}
                      </TableCell>
                      <TableCell>
                        <Link
                          href={`https://${getEmailDomain(c.email)}`}
                          target="_blank"
                        >
                          {getEmailDomain(c.email)}
                        </Link>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6">Verified Blitz Users</Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Company</TableCell>
                  <TableCell>Domain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(processedUsers).map(company => {
                  const domain = getEmailDomain(
                    processedUsers[company][0].email
                  )
                  return (
                    <TableRow key={company}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(company)}
                          onChange={() => toggle(company)}
                        />
                      </TableCell>
                      <TableCell>{company}</TableCell>
                      <TableCell>
                        <Link href={`https://${domain}`} target="_blank">
                          {domain}
                        </Link>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {selected.length > 0 && (
            <Fab
              color="primary"
              sx={{ position: 'fixed', bottom: 16, right: 16 }}
              onClick={handleOpen}
            >
              <SendIcon />
            </Fab>
          )}

          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Send Pitch</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Craft your email and we’ll send it to the selected contacts.
              </DialogContentText>
              <TextField
                label="Subject"
                fullWidth
                sx={{ mb: 2 }}
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
              <TextField
                label="Body"
                fullWidth
                multiline
                rows={4}
                sx={{ mb: 2 }}
                value={body}
                onChange={e => setBody(e.target.value)}
              />
              <TextField
                label="Rate"
                fullWidth
                sx={{ mb: 2 }}
                value={rate}
                onChange={e => setRate(e.target.value)}
              />
              <TextField
                label="Your Email (CC)"
                fullWidth
                required
                value={ccEmail}
                onChange={e => setCcEmail(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={sendPitch}
                disabled={sending}
                startIcon={sending ? <CircularProgress size={18} /> : null}
              >
                Send
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  )
}
